import { useCallback, useState } from "react";
import { supabase } from "../lib/supabase";
import { notify } from "../lib/notifications";
import {
  PlanMilestone,
  PlanMilestoneFormData,
  ProjectPlan,
  ProjectTemplate,
} from "../pages/contractor/planTypes";

export function usePlan(projectId: string) {
  const [plan, setPlan] = useState<ProjectPlan | null>(null);
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlan = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);

    try {
      const [projectResult, milestonesResult] = await Promise.all([
        supabase
          .from("projects")
          .select("plan_status, plan_scope, plan_notes, plan_submitted_at, plan_approved_at")
          .eq("id", projectId)
          .single(),
        supabase
          .from("plan_milestones")
          .select("*")
          .eq("project_id", projectId)
          .order("milestone_order", { ascending: true }),
      ]);

      if (projectResult.error || milestonesResult.error) {
        setError(projectResult.error?.message ?? milestonesResult.error?.message ?? "Failed to fetch plan");
        return;
      }

      setPlan({
        ...(projectResult.data as Omit<ProjectPlan, "milestones">),
        milestones: (milestonesResult.data ?? []) as PlanMilestone[],
      });
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    const { data, error: err } = await supabase
      .from("project_templates")
      .select("id, name, description, job_type, template_milestones(id, template_id, title, description, milestone_order, default_percentage)")
      .order("name", { ascending: true });

    setTemplatesLoading(false);
    if (err) {
      setError(err.message);
      return;
    }

    setTemplates(
      (data ?? []).map((t) => ({
        ...t,
        milestones: (t.template_milestones ?? []).sort(
          (a: { milestone_order: number }, b: { milestone_order: number }) =>
            a.milestone_order - b.milestone_order,
        ),
      })) as ProjectTemplate[],
    );
  }, []);

  const savePlanDetails = useCallback(
    async (scope: string, notes: string) => {
      setSaving(true);
      setError(null);

      const { error: err } = await supabase
        .from("projects")
        .update({ plan_scope: scope || null, plan_notes: notes || null })
        .eq("id", projectId);

      setSaving(false);
      if (err) {
        setError(err.message);
        return false;
      }
      await fetchPlan();
      return true;
    },
    [projectId, fetchPlan],
  );

  const loadTemplate = useCallback(
    async (template: ProjectTemplate) => {
      setSaving(true);
      setError(null);

      // Delete existing plan milestones first
      const { error: deleteErr } = await supabase
        .from("plan_milestones")
        .delete()
        .eq("project_id", projectId);

      if (deleteErr) {
        setError(deleteErr.message);
        setSaving(false);
        return false;
      }

      const rows = template.milestones.map((m) => ({
        project_id: projectId,
        title: m.title,
        description: m.description,
        milestone_order: m.milestone_order,
        percentage_of_project: m.default_percentage,
        estimated_cost: null,
        target_date: null,
      }));

      const { error: insertErr } = await supabase
        .from("plan_milestones")
        .insert(rows);

      setSaving(false);
      if (insertErr) {
        setError(insertErr.message);
        return false;
      }

      await fetchPlan();
      return true;
    },
    [projectId, fetchPlan],
  );

  const addPlanMilestone = useCallback(
    async (data: PlanMilestoneFormData, currentMilestones: PlanMilestone[]) => {
      setSaving(true);
      setError(null);

      const nextOrder =
        currentMilestones.length > 0
          ? Math.max(...currentMilestones.map((m) => m.milestone_order)) + 1
          : 1;

      const { error: err } = await supabase.from("plan_milestones").insert({
        project_id: projectId,
        title: data.title.trim(),
        description: data.description.trim() || null,
        milestone_order: nextOrder,
        percentage_of_project: Number(data.percentage_of_project) || 0,
        estimated_cost: data.estimated_cost ? Number(data.estimated_cost) : null,
        target_date: data.target_date || null,
      });

      setSaving(false);
      if (err) {
        setError(err.message);
        return false;
      }
      await fetchPlan();
      return true;
    },
    [projectId, fetchPlan],
  );

  const updatePlanMilestone = useCallback(
    async (id: string, data: PlanMilestoneFormData) => {
      setSaving(true);
      setError(null);

      const { error: err } = await supabase
        .from("plan_milestones")
        .update({
          title: data.title.trim(),
          description: data.description.trim() || null,
          percentage_of_project: Number(data.percentage_of_project) || 0,
          estimated_cost: data.estimated_cost ? Number(data.estimated_cost) : null,
          target_date: data.target_date || null,
        })
        .eq("id", id);

      setSaving(false);
      if (err) {
        setError(err.message);
        return false;
      }
      await fetchPlan();
      return true;
    },
    [fetchPlan],
  );

  const deletePlanMilestone = useCallback(
    async (id: string) => {
      setSaving(true);
      setError(null);

      const { error: err } = await supabase
        .from("plan_milestones")
        .delete()
        .eq("id", id);

      setSaving(false);
      if (err) {
        setError(err.message);
        return false;
      }
      await fetchPlan();
      return true;
    },
    [fetchPlan],
  );

  const reorderPlanMilestones = useCallback(
    async (reordered: PlanMilestone[]) => {
      // Optimistic update so the UI reflects the change immediately
      setPlan((prev) => (prev ? { ...prev, milestones: reordered } : prev));

      await Promise.all(
        reordered.map((m, idx) =>
          supabase
            .from("plan_milestones")
            .update({ milestone_order: idx + 1 })
            .eq("id", m.id),
        ),
      );
    },
    [],
  );

  const submitForApproval = useCallback(async () => {
    setSaving(true);
    setError(null);

    const { error: err } = await supabase
      .from("projects")
      .update({
        plan_status: "pending_approval",
        plan_submitted_at: new Date().toISOString(),
      })
      .eq("id", projectId);

    setSaving(false);
    if (err) {
      setError(err.message);
      return false;
    }
    await fetchPlan();
    return true;
  }, [projectId, fetchPlan]);

  const requestChanges = useCallback(async (notes: string) => {
    setSaving(true);
    setError(null);

    const { error: err } = await supabase
      .from("projects")
      .update({ plan_status: "draft" })
      .eq("id", projectId);

    setSaving(false);
    if (err) {
      setError(err.message);
      return false;
    }

    // Notify the contractor with the client's feedback
    const { data: project } = await supabase
      .from("projects")
      .select("contractor_id, title")
      .eq("id", projectId)
      .single();

    if (project) {
      notify({
        recipientId: project.contractor_id,
        projectId,
        type: "plan_changes_requested",
        title: "Plan changes requested",
        message: notes.trim()
          ? `Client requested changes: ${notes.trim()}`
          : `The client has sent the plan back for revisions on "${project.title}".`,
        link: `/contractor/projects/${projectId}`,
      });
    }

    await fetchPlan();
    return true;
  }, [projectId, fetchPlan]);

  // Called by client to approve — delegates to the approve-plan edge function
  // which runs with service-role privileges to write milestones (client RLS only allows SELECT).
  const approvePlan = useCallback(
    async (_planMilestones: PlanMilestone[]) => {
      setSaving(true);
      setError(null);

      const { error: fnError } = await supabase.functions.invoke("approve-plan", {
        body: { projectId },
      });

      setSaving(false);
      if (fnError) {
        setError(fnError.message);
        return false;
      }
      await fetchPlan();
      return true;
    },
    [projectId, fetchPlan],
  );

  return {
    plan,
    templates,
    loading,
    templatesLoading,
    saving,
    error,
    fetchPlan,
    fetchTemplates,
    savePlanDetails,
    loadTemplate,
    addPlanMilestone,
    updatePlanMilestone,
    deletePlanMilestone,
    reorderPlanMilestones,
    submitForApproval,
    requestChanges,
    approvePlan,
  };
}
